import React, { useState, useEffect } from 'react';
import './YouTubePlayer.css';

/**
 * YouTubePlayer
 * -------------
 * Renders a YouTube embed in one of two layouts:
 *
 *   - position="inline"   (default)
 *       Full-width gradient card embedded in the page flow. Used historically
 *       between the genre grid and the trending stories section.
 *
 *   - position="floating"
 *       A small fixed-position card anchored to the bottom-right of the
 *       viewport. Stays visible while the user scrolls. Width is
 *       responsive (360px desktop, 240px tablet, 180px mobile). Closed via
 *       the ✕ button (floating mode remembers the closed state for the
 *       session).
 *
 * Props:
 *   - videoId: string (required) — the YouTube video id (the part after
 *     `?v=` in a watch URL, or after `/embed/`).
 *   - title:   string (optional) — caption + iframe title attribute.
 *   - position: 'inline' | 'floating' (default 'inline').
 */
function YouTubePlayer({ videoId, title, position = 'inline' }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayer, setShowPlayer] = useState(true);
  const [floatingClosed, setFloatingClosed] = useState(false);

  useEffect(() => {
    // Auto-play on first load (both layouts)
    const timer = setTimeout(() => {
      setIsPlaying(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setShowPlayer(false);
    setIsPlaying(false);
    if (position === 'floating') {
      setFloatingClosed(true);
    }
  };

  // Floating mode: if the user closed it once, stay closed for the session
  if (position === 'floating' && floatingClosed) {
    return null;
  }

  if (!showPlayer) {
    return null;
  }

  const containerClass =
    position === 'floating'
      ? 'youtube-player-container youtube-floating'
      : 'youtube-player-container';

  return (
    <div className={containerClass}>
      <div className="youtube-player-wrapper">
        <button
          className="youtube-close-btn"
          onClick={handleClose}
          aria-label="Close YouTube player"
          title="Close"
        >
          ✕
        </button>
        {isPlaying ? (
          <iframe
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0&controls=1&rel=0&modestbranding=1`}
            title={title || 'YouTube video player'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
        ) : (
          <div className="youtube-placeholder" onClick={() => setIsPlaying(true)}>
            <div className="play-button">▶</div>
            <img
              src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
              alt={title}
              className="youtube-thumbnail"
            />
          </div>
        )}
      </div>
      <div className="youtube-caption">
        <span>🎬 {title || 'Featured Video'}</span>
      </div>
    </div>
  );
}

export default YouTubePlayer;